/**
 * v7.18 — Dialog GLOBAL para captura do PIN admin.
 *
 * Escuta `window.addEventListener('kaven:admin-pin-required', ev)` e mostra
 * o modal. Quando o operador confirma, dispara
 * `kaven:admin-pin-result` com `{ id, pin }`. Cancelar dispara `{ id, pin: null }`.
 *
 * Mantenha 1 (uma) instância montada no App.js para servir todas as páginas.
 */
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ShieldCheck, Loader2 } from 'lucide-react';

export default function AdminPinDialog() {
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [reqId, setReqId] = useState(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    function onReq(ev) {
      setReqId(ev.detail?.id || null);
      setMessage(ev.detail?.message || 'Digite o PIN admin');
      setPin('');
      setErr('');
      setBusy(false);
      setOpen(true);
    }
    window.addEventListener('kaven:admin-pin-required', onReq);
    return () => window.removeEventListener('kaven:admin-pin-required', onReq);
  }, []);

  function emitResult(pinValue) {
    window.dispatchEvent(new CustomEvent('kaven:admin-pin-result', {
      detail: { id: reqId, pin: pinValue },
    }));
  }

  function handleClose(nextOpen) {
    if (!nextOpen && !busy) {
      emitResult(null);
      setOpen(false);
    }
  }

  function handleConfirm() {
    const trimmed = String(pin || '').trim();
    if (!trimmed) {
      setErr('PIN obrigatório');
      return;
    }
    setBusy(true);
    setErr('');
    // Quem chama o `ensureAdminToken` faz o adminLogin com o PIN devolvido
    // e captura o erro de PIN inválido. Aqui só emitimos.
    emitResult(trimmed);
    setOpen(false);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="admin-pin-dialog"
        className="max-w-sm bg-white border-2 border-slate-300 rounded-2xl"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900 font-bold">
            <ShieldCheck size={18} className="text-emerald-600" />
            Confirmação admin
          </DialogTitle>
          <DialogDescription className="text-slate-500 text-xs leading-relaxed">
            {message}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <Input
            data-testid="admin-pin-input"
            type="password"
            inputMode="numeric"
            autoComplete="off"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
            onKeyDown={(e) => e.key === 'Enter' && handleConfirm()}
            placeholder="••••"
            className="h-12 border-2 border-slate-200 rounded-xl text-center font-mono text-lg tracking-[0.4em]"
            autoFocus
          />
          {err && (
            <p data-testid="admin-pin-error" className="text-xs font-semibold text-red-600">{err}</p>
          )}
          <p className="text-[10px] text-slate-400 leading-relaxed">
            🔒 PIN é validado no backend e libera um token JWT de 30min para alterar configurações sensíveis. O PIN nunca é gravado no navegador.
          </p>
          <div className="flex gap-2">
            <button
              data-testid="admin-pin-cancel"
              onClick={() => handleClose(false)}
              disabled={busy}
              className="flex-1 h-11 rounded-xl border-2 border-slate-200 bg-white text-slate-600 font-bold text-sm hover:bg-slate-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              data-testid="admin-pin-confirm"
              onClick={handleConfirm}
              disabled={busy || !pin}
              className="flex-1 h-11 rounded-xl bg-emerald-600 text-white font-bold text-sm hover:bg-emerald-700 disabled:opacity-40 flex items-center justify-center gap-2"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : null}
              Confirmar
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
